import React, { createContext, FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useWizard, WizardContext } from 'react-wizard-primitive'

import { AppContext, CHECKOUT } from 'Const'
import useEvent from 'Hooks/useEvent'
import { CheckoutPayload } from 'Types'

import CustomerFieldsGroup, { FormState as CustomerFormState } from './CustomerFieldsGroup'
import FulfillmentFieldsGroup, { FormState as FulfillmentFormState } from './FulfillmentFieldsGroup'
import PaymentArea from './PaymentArea'
import PaymentProvider from './PaymentArea/Provider'

export const StepContext = createContext<{
  checkouting: boolean
  isLastStep: boolean
  nextStep(state: Partial<FormState>): void
  previousStep(stepIndex: number): void
}>({
  checkouting: false,
  isLastStep: false,
  nextStep: () => {},
  previousStep: () => {},
})

export type FormState = {
  customer?: CustomerFormState
  fulfillment?: FulfillmentFormState['fulfillment']
  locationId: FulfillmentFormState['locationId']
  shippingAddress: FulfillmentFormState['shippingAddress']
  squarePayment?: {
    sourceId: string
  }
}

const Form: FC = () => {
  const { shop, cart, onCheckout } = useContext(AppContext)

  const wizard = useWizard()
  const { activeStepIndex, resetToStep, nextStep: wizardNextStep } = wizard

  const [checkout, { loading: checkouting }] = useMutation<{ checkout: CheckoutPayload }>(CHECKOUT)

  const paymentStepIsEnabled = shop.isPaymentRequired || shop.isGiftCardAvailable

  const steps = useMemo(() => {
    const steps: number[] = [0] // NOTE: Customer step
    const addStep = () => steps.push(steps.length)
    if (cart.hasFulfillableItems) addStep()
    if (paymentStepIsEnabled) addStep()
    return steps
  }, [cart.hasFulfillableItems, paymentStepIsEnabled])

  const isLastStep = steps[steps.length - 1] === activeStepIndex

  const [formState, setFormState] = useState({} as Partial<FormState>)
  const previousStep = useCallback((stepIndex: number) => resetToStep(Math.max(stepIndex - 1, 0)), [resetToStep])
  const nextStep = useEvent((newState: Record<string, unknown>) => {
    const state = { ...formState, ...newState }
    if (isLastStep) void checkout({ variables: { input: state } }).then(({ data }) => onCheckout(data?.checkout))
    else wizardNextStep()
    setFormState(state)
  })

  useEffect(() => resetToStep(Math.min(activeStepIndex, steps.length - 1)), [steps])

  return (
    <StepContext.Provider value={{ checkouting, isLastStep, nextStep, previousStep }}>
      <div className="checkout-form">
        <WizardContext.Provider value={wizard}>
          <CustomerFieldsGroup />
          {cart.hasFulfillableItems ? <FulfillmentFieldsGroup /> : null}
          {paymentStepIsEnabled ? (
            <PaymentProvider>
              <PaymentArea fulfillmentKind={formState.fulfillment?.kind} />
            </PaymentProvider>
          ) : null}
        </WizardContext.Provider>
      </div>
    </StepContext.Provider>
  )
}

export default memo(Form)
